import type { CopilotAgentUsageMeterBody } from '@customerio/hydra-ui-api';
import { getApiClientErrorStatus, hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { useAuth } from '@/services/auth';

export type AgentUsageMeter = CopilotAgentUsageMeterBody;

/** Whether the error is the endpoint's "meter not enabled for this account" 404. */
function isMeterDisabled(error: unknown): boolean {
  return getApiClientErrorStatus(error) === 404;
}

/**
 * Loads the account's agent usage meter — the single read behind every meter
 * surface. The endpoint 404s when the account's meter feature flag is off;
 * that is the "surfaces stay hidden" signal, not an error, so it is neither
 * retried nor reported.
 */
export function useAgentUsageMeter() {
  const { account } = useAuth();

  // The default retry policy already skips 4xx, so the flag-off 404 is not retried.
  const query = useHydraUIAPIQuery({
    queryKey: ['getAgentUsageMeter', { params: { account_id: account.id } }],
    queryFn: ({ signal }) =>
      hydraUiApi.agentUsageMeter.list({
        params: { path: { account_id: String(account.id) } },
        signal,
      }),
  });

  // TanStack keeps the last successful data through an error, so a refetch
  // that 404s (flag turned off) must explicitly stop exposing the stale meter.
  const disabled = query.isError && isMeterDisabled(query.error);
  const meter: AgentUsageMeter | undefined = disabled ? undefined : query.data?.meter;
  return {
    meter,
    isLoading: query.isLoading,
    isError: query.isError && !disabled,
  };
}
